import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import FirebaseStore from 'stores/FirebaseStore';
import GroupShowStore from 'stores/GroupShowStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import TokenUpchargeDisclaimer from 'components/TokenUpchargeDisclaimer';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import {
  HOUR_AS_SECONDS,
  MINUTE_AS_MILLISECONDS,
  SECOND_AS_MILLISECONDS,
} from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import {
  BROADCAST_STATUS,
  NEW_SHOWS_STATE,
} from 'enum/videoPlayer';
import {
  generateID,
  getTimeStringWithHours,
  getTimeStringWithoutHours,
} from 'utils/helpers';
import useResize from 'hooks/useResize';

import groupShowTicketIcon from '../../../images/groupShowTicket.png';
import tokenIcon from '../../../images/icontokens.png';

import styles from './GroupShowWithoutTicket.css';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const IDS = {
  prefix: 'qWPo1381',
  element: 'groupShowWithoutTicket',
  drawerBuyTicket: 'drawerBuyTicket',
  buyTicket: 'buyTicket',
};
const makeId = generateID(IDS.prefix, IDS.element);

const SMALL_DESKTOP_THRESHOLD = 750;

// eslint-disable-next-line complexity
const GroupShowWithoutTicket = (props) => {
  const { roomName, user, showBuyTokensModal, isDesktop } = props;
  const { groupShowTicketPrice, newShowsState, newShowsDetails } = FirebaseStore;
  const { remainingTimeLeftUntillGroupShow, buyGroupShowTicket, getRemainingTimeUntillGroupShow } = GroupShowStore;
  const { isPlayerBroadcastMode } = ProfileStore;
  const { user: { sfw } } = UserStore;
  const [secondsCounter, setSecondsCounter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { balance } = PaymentStore;

  const areHoursVisible = Math.floor(remainingTimeLeftUntillGroupShow / HOUR_AS_SECONDS) > 0;
  let parsedSeconds = undefined;
  if (areHoursVisible) {
    parsedSeconds = getTimeStringWithHours(secondsCounter);
  } else {
    parsedSeconds = getTimeStringWithoutHours(secondsCounter);
  }

  const notEnoughBalance = balance < newShowsDetails.ticketPrice || user.username === null;
  const isAdmin = user.accessLevel >= ACCESS_LEVEL_NUM.ADMIN;
  const isInBonusTimeAndNotAdmin = newShowsState === NEW_SHOWS_STATE.BONUS_GS && !isAdmin;
  const ticketBtnLabel = isAdmin ? translate('v2profile.admin.ticket') : translate('v2profile.button.buyTicket');
  const showPublicChatCF = UserStore.hasCF(allUserGroups.PROFILES_V2_SPECIAL_SHOW_PUBLIC_CHAT);
  const isBuyTokensButton = (notEnoughBalance || user.username === null) && !isAdmin;
  const [width, height] = useResize();
  const smallDesktop = width && height < SMALL_DESKTOP_THRESHOLD;

  const disconnect = () => {
    const { setStreamHasEndedState, setBroadcastStatus } = ProfileStore;
    setStreamHasEndedState(true);
    setBroadcastStatus(BROADCAST_STATUS.RECONNECTING);
  };

  useEffect(() => {
    getRemainingTimeUntillGroupShow(newShowsDetails.currentStart + newShowsDetails.currentDuration);
  }, [newShowsDetails.currentStart, newShowsDetails.currentDuration, getRemainingTimeUntillGroupShow]);

  useEffect(() => {
    setSecondsCounter(remainingTimeLeftUntillGroupShow);
    const interval =
      remainingTimeLeftUntillGroupShow !== null &&
      setInterval(() => {
        setSecondsCounter((counter) => counter - 1);
      }, SECOND_AS_MILLISECONDS);

    return () => clearInterval(interval);
  }, [remainingTimeLeftUntillGroupShow]);

  useEffect(() => {
    if (
      newShowsState !== NEW_SHOWS_STATE.INSIDE_GS &&
      newShowsState !== NEW_SHOWS_STATE.BONUS_GS &&
      newShowsState !== NEW_SHOWS_STATE.PAUSED_GS
    ) {
      disconnect();
    }
  }, [newShowsState]);

  const handleBuyTokens = () => {
    showBuyTokensModal();
  };

  const getTicketData = (amount) => {
    return {
      tokens: amount,
      action: 'sendTokensForGroupShow',
      accessHash: user.accessHash,
      username: user.username,
      roomname: roomName,
    };
  };

  const handleBuyTicket = () => {
    const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (isGuestBilling) {
      ModalService.showModal(ModalService.MODAL_KEYS.GUEST_BILLING_MODAL, {
        close: ModalService.hideModal,
        show: true,
      });
    } else {
      setIsLoading(true);
      buyGroupShowTicket(getTicketData(newShowsDetails.ticketPrice), () => setIsLoading(false));
    }
  };

  const bottomWrapper = classNames({
    [styles.bonusBottomWrapper]: isInBonusTimeAndNotAdmin,
    [styles.bottomWrapper]: !isInBonusTimeAndNotAdmin,
  });
  const containerClass = classNames({
    [styles.container]: true,
    [styles.containerAlternate]: showPublicChatCF,
  });
  const avatarClasses = classNames({
    [styles.perfImg]: true,
    [styles.blur]: sfw,
  });

  const renderDesktopView = (
    <div className={styles.groupShowDesktopWrapper}>
      <div className={styles.groupShowInfoWrapper}>
        <div className={styles.groupShowName}>
          <p>{translate('v2profile.in.group.show.title', { 0: roomName })}</p>
        </div>
        <div>
          <div className={styles.groupShowDescription}>
            {!smallDesktop && (
              <img alt="groupshowTicket" className={styles.ticketImg} src={groupShowTicketIcon} />
            )}
            {isInBonusTimeAndNotAdmin && (
              <Fragment>
                <p>{translate('v2profile.group.show.ending.soon')}</p>
                <p>{translate('v2profile.check.back.in')}</p>
              </Fragment>
            )}
            {!isInBonusTimeAndNotAdmin && (
              <Fragment>
                <p className={styles.title}>{translate('v2profile.groupshow.join.show')}</p>
                <p className={styles.noMargin}>&apos;{newShowsDetails.showDescription}&apos;</p>
                <p className={styles.noMargin}>
                  {translate('v2profile.groupshow.duration',
                    { 0: Math.floor(newShowsDetails.gsTime / MINUTE_AS_MILLISECONDS) })}
                </p>
                <p>
                  {translate('v2profile.group.show.time.remaining')} - {parsedSeconds}
                </p>
                {!smallDesktop && (
                  <p>
                    {' '}
                    <img alt="token" className={styles.tokenImg} src={tokenIcon} />{' '}
                    {isAdmin ? 0 : groupShowTicketPrice}
                  </p>
                )}
                <TokenUpchargeDisclaimer className={styles.tokenUpchargeDisclaimer} />
                <LoadingButton
                  colorVariant="orange"
                  disabled={isPlayerBroadcastMode}
                  id={makeId(IDS.buyTicket)}
                  isLoading={isLoading}
                  onClick={isBuyTokensButton ? handleBuyTokens : handleBuyTicket}
                >
                  {smallDesktop && (
                    <span className={styles.buyTicketLabel}>
                      <img alt="token" className={styles.tokenImgSmall} src={tokenIcon} />{' '}
                      {isAdmin ? 0 : groupShowTicketPrice}
                    </span>
                  )}
                  {ticketBtnLabel}
                </LoadingButton>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderMobileView = (
    <div className={containerClass}>
      <div>
        {showPublicChatCF ? (
          <img alt="ticket" className={styles.mobileTicketImg} src={groupShowTicketIcon} />
        ) : (
          <img alt="performer" className={avatarClasses} src={`${BASE_URL}/avatar/user/60x60/${roomName}.jpg`} />
        )}
        <h2 className={styles.perfName}>{roomName}</h2>
        <div className={styles.redWrapper}>{translate('v2profile.group.show.in')}</div>
        <div className={styles.mobileDescWrapper}>
          {!showPublicChatCF &&
            <img alt="ticket" className={styles.mobileTicketImg} src={groupShowTicketIcon} />}
          {isInBonusTimeAndNotAdmin && (
            <Fragment>
              <div>{translate('v2profile.group.show.ending.soon')}</div>
              <p>{translate('v2profile.check.back.in')}</p>
            </Fragment>
          )}
          {!isInBonusTimeAndNotAdmin && (
            <Fragment>
              <div>&apos;{newShowsDetails.showDescription}&apos;</div>
              <div>
                {translate('v2profile.groupshow.duration',
                  { 0: Math.floor(newShowsDetails.gsTime / MINUTE_AS_MILLISECONDS) }
                )}
              </div>
            </Fragment>
          )}
        </div>
      </div>
      <div className={bottomWrapper}>
        {!isInBonusTimeAndNotAdmin && (
          <div className={styles.groupShowInfo}>
            <img alt="token" className={styles.tokenImg} src={tokenIcon} />
            <span>
              {isAdmin ? 0 : groupShowTicketPrice} {translate('v2profile.show.tokens')}
            </span>
            <p>
              {translate('v2profile.group.show.time.remaining')} - {parsedSeconds}
            </p>
            <TokenUpchargeDisclaimer className={styles.tokenUpchargeDisclaimer} />
            <LoadingButton
              colorVariant="orange"
              disabled={isPlayerBroadcastMode}
              id={makeId(IDS.buyTicket)}
              isLoading={isLoading}
              onClick={isBuyTokensButton ? handleBuyTokens : handleBuyTicket}
            >
              {ticketBtnLabel}
            </LoadingButton>
          </div>
        )}
      </div>
    </div>
  );

  return isDesktop ? renderDesktopView : renderMobileView;
};

GroupShowWithoutTicket.propTypes = {
  isDesktop: PropTypes.bool,
  isUserInRoster: PropTypes.bool,
  roomDataReady: PropTypes.bool,
  roomName: PropTypes.string,
  showBuyTokensModal: PropTypes.func,
  user: PropTypes.object,
  walletDetails: PropTypes.object,
};

export default observer(GroupShowWithoutTicket);
